import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { DialogConfirm, DialogConfirmData } from 'app/dialogs/confirm/dialog-confirm';
import { DialogJobsMessageDetails } from 'app/dialogs/jobs/message-details/dialog-jobs-message-details';
import { sortCompare } from 'app/helpers/sortCompare';
import { ExposedListJobMessagesPaginatedReq } from 'app/interfaces/requests/Exposed/exposedListJobMessagesPaginatedReq';
import { ExposedListJobsRes } from 'app/interfaces/responses/Exposed/exposedListJobsRes';
import { ExposedReadJobDetailsRes } from 'app/interfaces/responses/Exposed/exposedReadJobDetailsRes';
import { ListMessageLogPaginateRes } from 'app/interfaces/responses/List/listMessageLogPaginateRes';
import { JobsService } from 'app/services/api/queue/jobs.service';
import { TranslationService } from 'app/services/api/translation/translation.service';
import { JOB_STATUS, PROCESSING_STATUS, PROCESSING_STATUS_TRANSLATIONS } from 'common/constants/queue.constants';
import { Subscription, interval } from 'rxjs';

@Component({
    selector: 'job-details',
    templateUrl: './job-details.component.html',
    styleUrls: ['./job-details.component.scss'],
})
export class JobDetailsComponent implements OnInit {
    @ViewChild('tablePaginator') displayedDataPaginator: MatPaginator;
    dataSource: MatTableDataSource<ListMessageLogPaginateRes> = new MatTableDataSource<ListMessageLogPaginateRes>();
    displayedColumns: string[] = ['id', 'type', 'status', 'created_at', 'last_activity_at', 'retries', 'error', 'actions'];
    dataReloadSubscription: Subscription;
    filterStatuses: number[] = [];
    pageSize: number = 25;
    pageNumber: number = 0;
    jobDetails: ExposedReadJobDetailsRes;
    jobId: number;
    loading: boolean = false;
    totalNumberOfMessages: number = 0;
    processingStatus = PROCESSING_STATUS;
    jobStatus = JOB_STATUS;

    constructor(
        private route: ActivatedRoute,
        private jobsService: JobsService,
        private translationService: TranslationService,
        private dialog: MatDialog,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.dataSource.paginator = this.displayedDataPaginator;
        this.jobId = parseInt(this.route.snapshot.paramMap.get('id'));
        this.fetchJobDetails();
        this.fetchJobMessages(this.pageNumber, this.pageSize);
        this.dataReloadSubscription = interval(10 * 1000).subscribe(() => {
            if (!this.loading) {
                this.fetchJobDetails(false);
                this.fetchJobMessages(this.pageNumber, this.pageSize, false);
            }
        });
    }

    ngOnDestroy(): void {
        this.dataReloadSubscription.unsubscribe();
    }

    fetchJobDetails = (showLoader: boolean = true) => {
        this.loading = true;
        this.jobsService
            .getJobDetails(this.jobId, showLoader)
            .then((res) => {
                this.jobDetails = res;
                this.loading = false;
                if (this.jobDetails.status == JOB_STATUS.FINISHED) {
                    this.dataReloadSubscription.unsubscribe();
                }
                // Call detect changes to make sure angular updates the viewChild reference to the paginator
                this.cdr.detectChanges();
            })
            .catch(() => {
                this.loading = false;
            });
    };

    fetchJobMessages = (pageNumber: number, pageSize: number, showLoader: boolean = true, scrollToTop: boolean = false) => {
        this.jobsService
            .getJobMessages(
                this.jobId,
                {
                    Take: pageSize,
                    Skip: pageNumber * pageSize,
                    Statuses: this.stringifyFilterStatuses(),
                } as ExposedListJobMessagesPaginatedReq,
                showLoader
            )
            .then((res) => {
                this.dataSource = new MatTableDataSource(res);
                this.totalNumberOfMessages = res[0].totalNumberOfEntries;

                // Call detect changes to make sure angular updates the viewChild reference to the paginator
                this.cdr.detectChanges();

                if (scrollToTop) {
                    window.scrollTo(0, 0);
                }
            });
    };

    pageChanged(event: PageEvent) {
        this.pageSize = event.pageSize;
        this.pageNumber = event.pageIndex;
        this.fetchJobMessages(this.pageNumber, this.pageSize, true);
    }

    getStatusMessage = (item: ExposedListJobsRes) => {
        let statusLabel = PROCESSING_STATUS_TRANSLATIONS[item.status];
        return this.translationService.translate(statusLabel);
    };

    onRetryFailedMessage = (item: ListMessageLogPaginateRes) => {
        this.jobsService.rerunFailedJobMessages(this.jobDetails.id, [item.id]).subscribe(() => {
            this.fetchJobDetails();
            this.fetchJobMessages(this.pageNumber, this.pageSize);
        });
    };

    onRetryFailedMessages = () => {
        const dialogRef = this.dialog.open(DialogConfirm, {
            autoFocus: false,
            restoreFocus: false,
            width: '450px',
            disableClose: true,
            data: {
                title: this.translationService.translate('label_confirm_action'),
                message: this.translationService.translate('text_confirm_retry_job_messages'),
            } as DialogConfirmData,
        });

        dialogRef.afterClosed().subscribe((confirm: boolean) => {
            if (confirm) {
                this.jobsService.rerunFailedJobMessages(this.jobDetails.id);
            }
        });
    };

    onShowMessageDetails = (element: ListMessageLogPaginateRes) => {
        this.dialog.open(DialogJobsMessageDetails, {
            autoFocus: false,
            restoreFocus: false,
            width: '650px',
            disableClose: false,
            data: {
                message: element,
            },
        });
    };

    onFilterChanged = (status: number) => {
        if (this.filterStatuses.includes(status)) {
            this.filterStatuses = this.filterStatuses.filter((s) => s != status);
        } else {
            this.filterStatuses.push(status);
        }
        this.pageNumber = 0;
        this.fetchJobMessages(this.pageNumber, this.pageSize);
    };

    stringifyFilterStatuses() {
        let returnValue = ',';
        if (this.filterStatuses.length > 0) {
            this.filterStatuses.forEach((s) => {
                returnValue += s + ',';
            });
        } else {
            return (
                this.processingStatus.FAILED +
                ',' +
                this.processingStatus.PROCESSING +
                ',' +
                this.processingStatus.QUEUED +
                ',' +
                this.processingStatus.SUCCESSFUL +
                ',' +
                this.processingStatus.CANCELLED +
                ','
            );
        }
        return returnValue;
    }

    sortData(sort: Sort) {
        const data = this.dataSource.data.slice();
        if (!sort.active || sort.direction === '') {
            this.dataSource.data = data;
            return;
        }

        this.dataSource.data = data.sort((a, b) => {
            const isAsc = sort.direction === 'asc';

            switch (sort.active) {
                case 'id':
                    return sortCompare(a.id, b.id, isAsc);
                case 'status':
                    return sortCompare(a.status, b.status, isAsc);
                case 'type':
                    return sortCompare(a.type, b.type, isAsc);
                case 'created_at':
                    return sortCompare(a.createdAt, b.createdAt, isAsc);
                case 'retries':
                    return sortCompare(a.retries, b.retries, isAsc);
                case 'last_activity_at':
                    return sortCompare(a.timeStamp, b.timeStamp, isAsc);
                default:
                    return 0;
            }
        });
    }
}
